import { useConfig } from '@seek/libs-shared';
import { useMelwaysLink } from '@seek/melways-react';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Hidden,
  IconChevron,
  Inline,
  Stack,
  Text,
  TextLink,
  TextLinkButton,
  Tiles,
} from 'braid-design-system';
import { useState } from 'react';
import translations from './.vocab';

import { ROOT_PATH } from '../../../paths';
import * as styles from './CompaniesList.css';

const MAX_VISIBLE_COMPANIES = 5;

const CompanyLink = ({
  name,
  slug,
  id,
  trackCompanyProfileLinkClicked,
}: {
  id: string;
  name: string;
  slug: string;
  trackCompanyProfileLinkClicked: ({
    linkedCompanyId,
  }: {
    linkedCompanyId: string;
  }) => void;
}) => {
  const { language } = useConfig();
  const localize = useMelwaysLink();

  return (
    <Text tone="secondary" size="small" key={slug}>
      <TextLink
        href={`${localize({
          path: `/${ROOT_PATH}/${slug}`,
          language,
        })}`}
        weight="weak"
        onClick={() => trackCompanyProfileLinkClicked({ linkedCompanyId: id })}
      >
        {name}
      </TextLink>
    </Text>
  );
};

export const CompaniesList = ({
  title,
  allCompanies,
  trackCompanyProfileLinkClicked,
  isHiddenBelowTablet = true,
}: {
  allCompanies: { id: string; name: string; slug: string }[];
  title: string;
  trackCompanyProfileLinkClicked: ({
    linkedCompanyId,
  }: {
    linkedCompanyId: string;
  }) => void;
  isHiddenBelowTablet?: boolean;
}) => {
  const [hideTopCompanies, setHideTopCompanies] = useState(true);
  const { t } = useTranslations(translations);

  const companyList = (
    <Stack space="large">
      <Text weight="strong">{title}</Text>
      <Inline space={'small'}>
        {allCompanies.slice(0, MAX_VISIBLE_COMPANIES).map((company) => (
          <CompanyLink
            name={company.name}
            slug={company.slug}
            key={company.slug}
            id={company.id}
            trackCompanyProfileLinkClicked={trackCompanyProfileLinkClicked}
          />
        ))}
        {allCompanies.length > MAX_VISIBLE_COMPANIES && (
          <Box
            onClick={() => {
              setHideTopCompanies(!hideTopCompanies);
            }}
            cursor="pointer"
          >
            <Inline space={'xxsmall'}>
              <TextLinkButton>
                <Text tone="secondary" size="small">
                  {t('View all')}
                </Text>
              </TextLinkButton>
              <Box className={!hideTopCompanies && styles.rotatedStyle}>
                <Text size="small">
                  <IconChevron alignY="lowercase" />
                </Text>
              </Box>
            </Inline>
          </Box>
        )}
      </Inline>
      {allCompanies.length > MAX_VISIBLE_COMPANIES && (
        <Box
          style={{
            display: hideTopCompanies ? 'none' : 'unset',
            transition: 'transform 0.2s ease-in-out opacity 0.2s ease-in-out',
          }}
        >
          <Tiles columns={4} space={'medium'}>
            {allCompanies.slice(MAX_VISIBLE_COMPANIES).map((company) => (
              <CompanyLink
                name={company.name}
                slug={company.slug}
                key={company.slug}
                trackCompanyProfileLinkClicked={trackCompanyProfileLinkClicked}
                id={company.id}
              />
            ))}
          </Tiles>
        </Box>
      )}
    </Stack>
  );

  return isHiddenBelowTablet ? (
    <Hidden below="tablet">{companyList}</Hidden>
  ) : (
    companyList
  );
};
